import { styled } from '@mui/material/styles';
import Tooltip, { tooltipClasses, TooltipProps } from '@mui/material/Tooltip';
import {
  getFocusedRouteNameFromRoute,
  useNavigationState,
} from '@react-navigation/native';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, Platform } from 'react-native';

import { Box, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import { WebIcon } from '@components/shared/Icon/WebIcon';
import {
  Document,
  LocalFile,
  Project,
  ProjectMemberRole,
} from '@graphql/generated';
import theme from '@themes/theme';

const BootstrapTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.colors.grey05,
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.colors.grey05,
    padding: theme.spacing.xs,
    borderRadius: theme.spacing.xxs,
    fontSize: 12,
  },
}));

interface ProjectFilesSelectionBarProps {
  isSelectMode: boolean;
  selectedDocuments: Array<Document | LocalFile>;
  setIsSelectMode: (value: boolean) => void;
  setSelectedDocuments: (documents: Array<Document | LocalFile>) => void;
  isTeamOwnerOrAdmin: boolean;
  project?: Project | null;
  downloadFiles: (
    files: Array<Document | LocalFile>,
    arg1: boolean,
    arg2: boolean
  ) => void;
  isDownloading: boolean;
  shareLoading: boolean;
  showLinkCopied: boolean;
  handleSharePress: () => Promise<void> | void;
  setShowDeleteModal: (value: boolean) => void;
}

export const ProjectFilesSelectionBar: React.FC<
  ProjectFilesSelectionBarProps
> = ({
  isSelectMode,
  selectedDocuments,
  setIsSelectMode,
  setSelectedDocuments,
  isTeamOwnerOrAdmin,
  project,
  downloadFiles,
  isDownloading,
  shareLoading,
  showLinkCopied,
  handleSharePress,
  setShowDeleteModal,
}) => {
  const { t } = useTranslation('models');

  const currentRouteName = useNavigationState((state) => {
    if (!state) return null;

    // Find the projects-stack route
    const projectsStackRoute = state.routes.find(
      (route) => route.name === 'projects-stack'
    );

    if (!projectsStackRoute?.state) return null;

    // Find the 'details' screen within projects-stack
    const detailsRoute = projectsStackRoute.state.routes.find(
      (route) => route.name === 'details'
    );

    if (!detailsRoute?.state) return null;

    // Get the focused route name from the tab navigator
    const routeName = getFocusedRouteNameFromRoute(detailsRoute);
    return routeName ?? null;
  });

  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  const pathnameCheck =
    /^\/?projects\/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}\/files\/?$/.test(
      path
    );

  const isProjectFilesRoute = currentRouteName === 'files' || pathnameCheck;

  if (!isSelectMode || !isProjectFilesRoute) {
    return <></>;
  }

  return (
    <Box
      position='absolute'
      bottom={0}
      backgroundColor='grey05'
      paddingHorizontal='l'
      paddingVertical='m'
      style={{
        left: -theme.spacing.s,
        right: -theme.spacing.s,
      }}>
      <Box
        flexDirection='row'
        alignItems='center'
        justifyContent='space-between'>
        <Box flexDirection='row' alignItems='center'>
          <Icon
            name='crossCircle2'
            color='white'
            variant='m'
            accessibilityLabel='Cancel selection'
            onPress={() => {
              setSelectedDocuments([]);
              setIsSelectMode(false);
            }}
          />
          <Text variant='body' color='white' marginLeft='xs'>
            {t('models:files.projectFilesSelected', {
              count: selectedDocuments.length,
            })}
          </Text>
        </Box>

        <Box flexDirection='row' alignItems='center' gap='l'>
          {(isTeamOwnerOrAdmin ||
            project?.currentUserRole !== ProjectMemberRole.Viewer) && (
            <BootstrapTooltip title='Delete' placement='top'>
              <Box>
                <Icon
                  name='Trash2'
                  color='white'
                  variant='l'
                  accessibilityLabel='Delete selected files'
                  onPress={() => {
                    setShowDeleteModal(true);
                  }}
                />
              </Box>
            </BootstrapTooltip>
          )}
          <Box
            alignItems='center'
            justifyContent='center'
            style={{ width: 24, height: 24 }}>
            {isDownloading ? (
              <ActivityIndicator color='white' />
            ) : (
              <BootstrapTooltip title='Download' placement='top'>
                <Box>
                  <Icon
                    name='Download'
                    color='white'
                    variant='l'
                    accessibilityLabel='Download selected files'
                    onPress={() => {
                      downloadFiles(selectedDocuments, false, false);
                    }}
                  />
                </Box>
              </BootstrapTooltip>
            )}
          </Box>
          {shareLoading ? (
            <Box paddingHorizontal='s'>
              <ActivityIndicator color='white' />
            </Box>
          ) : (
            <Box position='relative'>
              {showLinkCopied && (
                <Box
                  position='absolute'
                  backgroundColor='white'
                  paddingHorizontal='s'
                  paddingVertical='xs'
                  borderRadius='xs'
                  style={{
                    bottom: 32,
                    right: -8,
                    zIndex: 10000,
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                    ...(Platform.select({
                      web: {
                        width: 'max-content',
                        whiteSpace: 'nowrap',
                      },
                    }) as any),
                  }}>
                  <Box
                    flexDirection='row'
                    alignItems='center'
                    gap='xs'
                    flexShrink={0}>
                    <Box
                      borderWidth={1}
                      borderColor='greenSecondary'
                      alignItems='center'
                      justifyContent='center'
                      style={{
                        width: 16,
                        height: 16,
                        borderRadius: 8,
                        flexShrink: 0,
                      }}>
                      <Icon name='Check' color='greenSecondary' variant='xs' />
                    </Box>
                    <Text
                      variant='labelSmall'
                      color='textPrimary'
                      numberOfLines={1}>
                      Link copied!
                    </Text>
                  </Box>
                </Box>
              )}
              <BootstrapTooltip title='Copy Link' placement='top'>
                <Box>
                  <WebIcon
                    isWhiteIcon
                    name='Link'
                    color='white'
                    variant='l'
                    accessibilityLabel='Share selected files'
                    onPress={() => {
                      void handleSharePress();
                    }}
                  />
                </Box>
              </BootstrapTooltip>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};
